import { Close } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Grid, IconButton, List, ListItem, Modal, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateQuickTextMutation, useDeleteQuickTextMutation, usePatchQuickTextMutation } from './chat.queries';
import { ChatTemplate } from './ChatModal';
import { EditTemplateModal } from './EditTemplateModal';

interface QuickTextsModalProps {
  open: boolean;
  onClose: () => void;
  templates: ChatTemplate[];
  onSelectText: (text: string) => void;
}

export const QuickTextsModal = ({ open, onClose, templates, onSelectText }: QuickTextsModalProps): ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [editTemplateOpen, setEditTemplateOpen] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<ChatTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Delete mutation with auto-refresh
  const deleteMutation = useDeleteQuickTextMutation(
    () => {
      enqueueSnackbar(t('chat.quickTexts.deleteSuccess'), { variant: 'success' });
      setDeletingId(null);
    },
    (error) => {
      enqueueSnackbar(error.message || t('chat.quickTexts.deleteError'), { variant: 'error' });
      setDeletingId(null);
    }
  );

  // Create mutation with auto-refresh
  const createMutation = useCreateQuickTextMutation(
    () => {
      enqueueSnackbar(t('chat.quickTexts.createSuccess'), { variant: 'success' });
      setEditTemplateOpen(false);
      setEditingTemplate(null);
    },
    (error) => {
      enqueueSnackbar(error.message || t('chat.quickTexts.createError'), { variant: 'error' });
    }
  );

  // Patch mutation with auto-refresh
  const patchMutation = usePatchQuickTextMutation(
    () => {
      enqueueSnackbar(t('chat.quickTexts.updateSuccess'), { variant: 'success' });
      setEditTemplateOpen(false);
      setEditingTemplate(null);
    },
    (error) => {
      enqueueSnackbar(error.message || t('chat.quickTexts.updateError'), { variant: 'error' });
    }
  );

  const quickTextStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 'sm',
    width: '95%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '4px',
    p: '8px 16px',
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  const handleEditTemplate = (template: ChatTemplate): void => {
    setEditingTemplate(template);
    setEditTemplateOpen(true);
  };

  const handleDeleteTemplate = (templateId: string): void => {
    setDeletingId(templateId);
    deleteMutation.mutate(templateId);
  };

  const handleSaveTemplate = (text: string): void => {
    if (editingTemplate?.id) {
      // Update existing template
      patchMutation.mutate({ id: editingTemplate.id, text });
    } else {
      // Create new template
      createMutation.mutate(text);
    }
  };

  const handleCreateTemplate = (): void => {
    setEditingTemplate(null);
    setEditTemplateOpen(true);
    console.log('Create template');
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Grid container sx={quickTextStyle}>
          <Grid item sx={{ marginTop: '6px', width: '100%', position: 'relative' }}>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: -8,
                top: -8,
              }}
            >
              <Close />
            </IconButton>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingRight: '32px' }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: '600 !important', color: theme.palette.primary.main, marginBottom: '4px' }}
              >
                {t('chat.quickTexts.title')}
              </Typography>
            </Box>
            <Typography variant="body2">{t('chat.quickTexts.description')}</Typography>
          </Grid>
          <Grid item>
            <List sx={{ padding: 0 }}>
              {(templates || []).map((template) => (
                <ListItem
                  key={template.id || Math.random().toString()}
                  sx={{
                    padding: 1,
                    my: '12px',
                    backgroundColor: 'rgba(77, 21, 183, 0.04)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" onClick={() => onSelectText(template.text)} sx={{ flex: 1 }}>
                    {template.text}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTemplate(template);
                      }}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(template.id);
                      }}
                      disabled={deletingId === template.id}
                      sx={{ color: theme.palette.error.main }}
                    >
                      {deletingId === template.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '16px' }}>
            <LoadingButton
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 500,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              variant="contained"
              fullWidth
              onClick={handleCreateTemplate}
            >
              {t('chat.quickTexts.createTemplate')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Modal>
      <EditTemplateModal
        open={editTemplateOpen}
        onClose={() => {
          setEditTemplateOpen(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
        onSave={handleSaveTemplate}
        loading={createMutation.isPending || patchMutation.isPending}
      />
    </>
  );
};
